import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { fmtINR, fmtNum, fmtDateTime } from '@/lib/api';
import { MapView } from '@/components/MapView';
import { Loading } from '@/components/Shared';
import { StatusBadge } from '@/components/Shared';
import { Button } from '@/components/ui/button';
import { Phone, Truck, Clock, Navigation, CheckCircle2, KeyRound } from 'lucide-react';

const STEPS = ['placed', 'confirmed', 'driver_assigned', 'en_route', 'arrived', 'delivery_started', 'completed', 'invoice_generated'];
const LABELS = { placed: 'Order Placed', confirmed: 'Confirmed', driver_assigned: 'Driver Assigned', en_route: 'Driver En Route', arrived: 'Arrived', delivery_started: 'Delivery Started', completed: 'Delivery Completed', invoice_generated: 'Invoice Generated' };

export default function OrderTrack() {
  const { orderId } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [track, setTrack] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    const [o, t] = await Promise.all([api.get(`/orders/${orderId}`), api.get(`/orders/${orderId}/track`)]);
    setOrder(o.data); setTrack(t.data);
  }, [orderId]);
  useEffect(() => { load(); timer.current = setInterval(load, 5000); return () => clearInterval(timer.current); }, [load]);
  // Stable route array so the map doesn't re-render needlessly.
  const mapRoute = useMemo(() => (track ? [track.origin, track.driver_position, track.destination] : []), [track]);
  if (!order || !track) return <Loading />;

  const curIdx = STEPS.indexOf(order.status);
  const showMap = ['driver_assigned', 'en_route', 'arrived', 'delivery_started'].includes(order.status);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div><h2 className="font-display text-xl font-semibold">{order.order_no}</h2><p className="text-sm text-muted-foreground">{fmtNum(order.pricing.qty)} {order.pricing.unit} · {order.pricing.product_name}</p></div>
        <StatusBadge status={order.status} label={order.status_label} />
      </div>

      {order.status === 'completed' || order.status === 'invoice_generated' ? (
        <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-3">
          <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          <div><p className="font-semibold">Delivered successfully</p><p className="text-sm text-muted-foreground">Thank you! Your invoice is ready.</p></div>
          {order.invoice_id && <Button className="ml-auto" size="sm" onClick={() => nav('/app/invoices')}>View Invoice</Button>}
        </div>
      ) : null}

      {showMap && (
        <div>
          <MapView driver={track.driver_position} destination={track.destination} origin={track.origin} route={mapRoute} height="300px" />
          <div className="grid grid-cols-3 gap-2 mt-2">
            <Stat icon={Clock} label="ETA" value={track.eta_min > 0 ? `${track.eta_min} min` : 'Arrived'} />
            <Stat icon={Navigation} label="Distance" value={`${track.distance_remaining_km} km`} />
            <Stat icon={Truck} label="Vehicle" value={track.driver?.vehicle_number || '-'} />
          </div>
        </div>
      )}

      {track.driver && showMap && (
        <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-3" data-testid="driver-info-card">
          <img src={track.driver.photo} alt="driver" className="h-12 w-12 rounded-full object-cover bg-muted" />
          <div className="flex-1"><p className="font-semibold">{track.driver.name}</p><p className="text-xs text-muted-foreground">{track.driver.masked_phone}</p></div>
          <Button size="icon" variant="secondary" onClick={() => window.location.href = `tel:${track.driver.phone}`} data-testid="call-driver-button"><Phone className="h-4 w-4" /></Button>
        </div>
      )}

      {['driver_assigned', 'en_route', 'arrived', 'delivery_started'].includes(order.status) && (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 flex items-center gap-3" data-testid="delivery-otp-card">
          <KeyRound className="h-5 w-5 text-primary" />
          <div><p className="text-sm font-medium">Share this OTP with driver on arrival</p><p className="font-display text-2xl font-bold tracking-widest" data-testid="customer-delivery-otp">{order.delivery_otp}</p></div>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card p-4" data-testid="order-status-timeline">
        <p className="font-semibold mb-3">Order Status</p>
        <ol className="relative border-l-2 border-border ml-2 space-y-4">
          {STEPS.map((s, i) => {
            const done = i <= curIdx;
            const tl = (order.timeline || []).find((t) => t.status === s);
            return (
              <li key={s} className="ml-4">
                <span className={`absolute -left-[9px] h-4 w-4 rounded-full border-2 border-white ${done ? 'bg-primary' : 'bg-muted'}`} />
                <p className={`text-sm ${done ? 'font-medium' : 'text-muted-foreground'}`}>{LABELS[s]}</p>
                {tl && <p className="text-xs text-muted-foreground">{fmtDateTime(tl.at)}</p>}
              </li>
            );
          })}
        </ol>
      </div>

      <div className="rounded-xl border border-border bg-card p-4 space-y-1.5 text-sm">
        <div className="flex justify-between"><span className="text-muted-foreground">Delivery Address</span><span className="text-right max-w-[60%]">{order.address_text}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="font-semibold">{fmtINR(order.pricing.total)}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Payment</span><span>{order.payment_method}</span></div>
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-lg border border-border bg-card p-2.5 text-center"><Icon className="h-4 w-4 mx-auto text-primary" /><p className="text-[11px] text-muted-foreground mt-1">{label}</p><p className="text-sm font-semibold">{value}</p></div>
);
